/**
 * "Your business in. A 9:16 video out." — the three moments of the product
 * shown as the product's own artefacts rather than as three text cards: the
 * Business Profile Mouja builds, the direction it recommends, and the 9:16
 * frame it makes. One demonstration business runs through all three, named
 * as a demo in the eyebrow; the tide line joins them.
 *
 * The section's copy (heading, lead, the three step titles and bodies) is the
 * approved copy in `translations.ts`; the artefact contents are a fixture of
 * the same demonstration business the story section uses.
 */
import { useLanguage } from '../i18n/language.js';

const DEMO = {
  en: {
    demo: 'Demo business',
    profile: {
      label: 'Business Profile',
      name: 'Bayt al-Oud',
      niche: 'Travel-size oud and attar for people who fly with their scent.',
      geography: 'Riyadh, Saudi Arabia',
      offers: ['Travel oud 12 ml', 'Attar sets', 'Burner kits'],
      status: 'Confirmed',
    },
    direction: {
      label: 'Recommended direction',
      rank: 'Direction 1',
      idea: 'The scent that boards with you',
      hookLabel: 'Opens with',
      hook: 'ريحتك تسافر معك… من البوابة لين الوصول',
      format: '9:16 · 20 seconds · TikTok and Reels',
      why: 'Uses only offers in the confirmed profile.',
    },
    video: {
      label: 'Video',
      status: 'Ready',
      meta: '0:20 · Arabic · 9:16',
      made: 'Made by Mouja',
    },
  },
  ar: {
    demo: 'نشاط تجريبي',
    profile: {
      label: 'الملف التجاري',
      name: 'بيت العود',
      niche: 'عود وعطور بحجم السفر للي يسافر وريحته معه.',
      geography: 'الرياض، السعودية',
      offers: ['عود سفر ١٢ مل', 'أطقم عطور', 'أطقم مباخر'],
      status: 'مؤكد',
    },
    direction: {
      label: 'الاتجاه اللي نرشّحه',
      rank: 'الاتجاه ١',
      idea: 'العطر اللي يركب معك',
      hookLabel: 'يبدأ بـ',
      hook: 'ريحتك تسافر معك… من البوابة لين الوصول',
      format: '9:16 · ٢٠ ثانية · TikTok وReels',
      why: 'يستخدم بس العروض اللي في ملفك المؤكد.',
    },
    video: {
      label: 'الفيديو',
      status: 'جاهز',
      meta: '0:20 · عربي · 9:16',
      made: 'سوّته موجة',
    },
  },
} as const;

export function ProcessSection(): React.ReactElement {
  const { copy, language } = useLanguage();
  const demo = DEMO[language];
  const steps = copy.process.steps;

  return (
    <section
      className="mj-process"
      id="how-it-works"
      data-landing-screen="2"
      aria-labelledby="process-title"
    >
      <div className="mj-process-inner">
        <div className="mj-process-copy">
          <p className="mj-process-eyebrow">
            <span className="mj-process-eyebrow-dot" aria-hidden="true" />
            {demo.demo} · {demo.profile.name}
          </p>
          <h2 id="process-title">{copy.process.heading}</h2>
          <p>{copy.process.lead}</p>
        </div>

        <ol className="mj-process-artefacts">
          <li className="mj-process-artefact">
            <article className="mj-process-card" aria-label={demo.profile.label}>
              <header className="mj-process-card-head">
                <span>{demo.profile.label}</span>
                <span className="mj-process-pill is-success">
                  {demo.profile.status}
                </span>
              </header>
              <strong className="mj-process-card-title">{demo.profile.name}</strong>
              <p className="mj-process-card-body">{demo.profile.niche}</p>
              <dl className="mj-process-facts">
                <div>
                  <dt>{language === 'ar' ? 'الموقع' : 'Geography'}</dt>
                  <dd>{demo.profile.geography}</dd>
                </div>
                <div>
                  <dt>{language === 'ar' ? 'العروض' : 'Offers'}</dt>
                  <dd>
                    {demo.profile.offers.map((offer) => (
                      <span className="mj-process-chip" key={offer}>
                        {offer}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>
            </article>
            <span className="mj-process-tide" aria-hidden="true" />
            <div className="mj-process-caption">
              <span>01</span>
              <h3>{steps[0]?.[0]}</h3>
              <p>{steps[0]?.[1]}</p>
            </div>
          </li>

          <li className="mj-process-artefact">
            <article
              className="mj-process-card is-direction"
              aria-label={demo.direction.label}
            >
              <header className="mj-process-card-head">
                <span>{demo.direction.rank}</span>
                <span className="mj-process-pill is-accent">
                  {demo.direction.label}
                </span>
              </header>
              <strong className="mj-process-card-title">{demo.direction.idea}</strong>
              <div className="mj-process-hook">
                <small>{demo.direction.hookLabel}</small>
                <b lang="ar" dir="rtl">
                  {demo.direction.hook}
                </b>
              </div>
              <p className="mj-process-card-meta">{demo.direction.format}</p>
              <p className="mj-process-card-body">{demo.direction.why}</p>
            </article>
            <span className="mj-process-tide" aria-hidden="true" />
            <div className="mj-process-caption">
              <span>02</span>
              <h3>{steps[1]?.[0]}</h3>
              <p>{steps[1]?.[1]}</p>
            </div>
          </li>

          <li className="mj-process-artefact">
            <article className="mj-process-card is-video" aria-label={demo.video.label}>
              <div className="mj-process-frame" aria-hidden="true">
                <span className="mj-process-frame-top">
                  <i />
                  {demo.video.made}
                </span>
                <b lang="ar" dir="rtl" className="mj-process-frame-hook">
                  {demo.direction.hook}
                </b>
                <span className="mj-process-frame-bottom">
                  <span className="mj-process-pill is-success">
                    {demo.video.status}
                  </span>
                  <em>{demo.video.meta}</em>
                </span>
              </div>
            </article>
            <span className="mj-process-tide" aria-hidden="true" />
            <div className="mj-process-caption">
              <span>03</span>
              <h3>{steps[2]?.[0]}</h3>
              <p>{steps[2]?.[1]}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>
  );
}
